import { Color3, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { PolyhedronBuilder } from '@babylonjs/core/Meshes/Builders/polyhedronBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Logger } from '@babylonjs/core/Misc/logger';
import { Observable } from '@babylonjs/core/Misc/observable';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Nullable } from '@babylonjs/core/types';
import { SpaceNode } from '@models/classes/meshes/space-node';
import { KbVector } from '@models/classes/primitives/kb-vector';
import { KbViewer } from '@view/kb-viewer';
import { KbAxisScaleGizmo } from './axis-scale-gizmo';
import { KbGizmo } from './gizmo';
/**
 * Gizmo that enables scaling a mesh along 3 axis
 */
export class KbScaleGizmo extends KbGizmo {
    /**
     * Internal gizmo used for interactions on the x axis
     */
    public xGizmo: KbAxisScaleGizmo;
    /**
     * Internal gizmo used for interactions on the y axis
     */
    public yGizmo: KbAxisScaleGizmo;
    /**
     * Internal gizmo used for interactions on the z axis
     */
    public zGizmo: KbAxisScaleGizmo;

    /**
     * Internal gizmo used to scale all axis equally
     */
    public uniformScaleGizmo: KbAxisScaleGizmo;

    /** Fires an event when any of it's sub gizmos are dragged */
    public onDragStartObservable = new Observable<void>();
    /** Fires an event while any of it's sub gizmos are dragged */
    public onDragObservable = new Observable<{ delta: Vector3 }>();
    /** Fires an event when any of it's sub gizmos are released from dragging */
    public onDragEndObservable = new Observable<{ delta: Vector3 }>();

    private _attachedSpaceNode: SpaceNode | undefined;
    private _positionSpace: TransformNode | undefined;

    public gizmoOffset: KbVector | undefined;

    attachTo(target: SpaceNode | KbVector, positionSpace?: SpaceNode) {
        if (!positionSpace && target instanceof SpaceNode && target._parent) {
            positionSpace = target._parent;
        }
        if (positionSpace) {
            this._positionSpace = this.kbViewer.getRendererNode(positionSpace);
        }
        if (target instanceof SpaceNode) {
            this._attachedSpaceNode = target;
            this._updateGizmoPosition();
        } else {
            this._updateGizmoPosition(target);
        }
    }
    updateGizmoPosition(changeTarget?: KbVector) {
        // Need to wait for the next frame because this can be called before the babylon object is actually updated
        this.kbViewer.onNextFrame(() => {
            this._updateGizmoPosition(changeTarget);
        });
    }

    private _updateGizmoPosition(changeTarget?: KbVector) {
        const positionSpaceTransform = this._positionSpace?.computeWorldMatrix(true);
        if (changeTarget) {
            // If target is a vector, then we want to rotate it as a direction, so set the gizmo position to the origin to calculations are done from there
            // But store the position as the offset because we want the gizmo itself to be visually placed on the position space origin
            this.attachMeshAtNodeSpace(this.gizmoLayer.utilityLayerScene, changeTarget, positionSpaceTransform);

            this.gizmoOffset = KbVector.FromVec3(this.attachedMesh!.position);
        } else if (this._attachedSpaceNode) {
            this.attachMeshAtNodeSpace(
                this.gizmoLayer.utilityLayerScene,
                this._attachedSpaceNode,
                positionSpaceTransform
            );

            // Put the gizmo in the same position as the pivot, in world space
            this.attachedMesh!.position.addInPlace(this._attachedSpaceNode.pivot.toVec3());
        }
        super.updateGizmoPosition();
    }

    public get attachedMesh() {
        return this.xGizmo.attachedMesh;
    }
    public set attachedMesh(mesh: Nullable<AbstractMesh>) {
        if (this.xGizmo) {
            this.xGizmo.attachedMesh = mesh;
            this.yGizmo.attachedMesh = mesh;
            this.zGizmo.attachedMesh = mesh;
            this.uniformScaleGizmo.attachedMesh = mesh;
        }
    }

    private scaleChange = Vector3.Zero();

    /**
     * Creates a ScaleGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     */
    constructor(
        private kbViewer: KbViewer,
        gizmoLayer: UtilityLayerRenderer = UtilityLayerRenderer.DefaultUtilityLayer
    ) {
        super(gizmoLayer);
        this.xGizmo = new KbAxisScaleGizmo(new Vector3(1, 0, 0), Color3.Red().scale(0.5), gizmoLayer);
        this.yGizmo = new KbAxisScaleGizmo(new Vector3(0, 1, 0), Color3.Green().scale(0.5), gizmoLayer);
        this.zGizmo = new KbAxisScaleGizmo(new Vector3(0, 0, 1), Color3.Blue().scale(0.5), gizmoLayer);

        // Create uniform scale gizmo
        this.uniformScaleGizmo = new KbAxisScaleGizmo(new Vector3(0, 1, 0), Color3.Yellow().scale(0.5), gizmoLayer);
        this.uniformScaleGizmo.updateGizmoRotationToMatchAttachedMesh = false;
        this.uniformScaleGizmo.uniformScaling = true;
        const uniformScalingMesh = PolyhedronBuilder.CreatePolyhedron(
            '',
            { type: 1 },
            this.uniformScaleGizmo.gizmoLayer.utilityLayerScene
        );
        uniformScalingMesh.scaling.scaleInPlace(0.03);
        uniformScalingMesh.visibility = 0;
        const octahedron = PolyhedronBuilder.CreatePolyhedron(
            '',
            { type: 1 },
            this.uniformScaleGizmo.gizmoLayer.utilityLayerScene
        );
        octahedron.scaling.scaleInPlace(0.007);
        uniformScalingMesh.addChild(octahedron);
        this.uniformScaleGizmo.setCustomMesh(uniformScalingMesh, true);
        // var light = gizmoLayer._getSharedGizmoLight();
        // light.includedOnlyMeshes = light.includedOnlyMeshes.concat(octahedron);

        // Relay drag events
        [this.xGizmo, this.yGizmo, this.zGizmo, this.uniformScaleGizmo].forEach(gizmo => {
            gizmo.dragBehavior.onDragStartObservable.add(() => {
                this.onDragStartObservable.notifyObservers();
                this.scaleChange.setAll(0);
            });
            gizmo.updateObservable.add(event => {
                this.scaleChange.addInPlace(event.delta);
                this.onDragObservable.notifyObservers({ delta: event.delta });
            });
            gizmo.dragBehavior.onDragEndObservable.add(() => {
                this.onDragEndObservable.notifyObservers({ delta: this.scaleChange });
            });
        });

        this.attachedMesh = null;
    }

    public set updateGizmoRotationToMatchAttachedMesh(value: boolean) {
        if (!value) {
            Logger.Warn('Setting updateGizmoRotationToMatchAttachedMesh = false on scaling gizmo is not supported.');
        }
        if (this.xGizmo) {
            this.xGizmo.updateGizmoRotationToMatchAttachedMesh = value;
            this.yGizmo.updateGizmoRotationToMatchAttachedMesh = value;
            this.zGizmo.updateGizmoRotationToMatchAttachedMesh = value;
        }
    }
    public get updateGizmoRotationToMatchAttachedMesh() {
        return this.xGizmo.updateGizmoRotationToMatchAttachedMesh;
    }

    /**
     * Drag distance in babylon units that the gizmo will snap to when dragged (Default: 0)
     */
    public set snapDistance(value: number) {
        if (this.xGizmo) {
            this.xGizmo.snapDistance = value;
            this.yGizmo.snapDistance = value;
            this.zGizmo.snapDistance = value;
            this.uniformScaleGizmo.snapDistance = value;
        }
    }
    public get snapDistance() {
        return this.xGizmo.snapDistance;
    }

    /**
     * Ratio for the scale of the gizmo (Default: 1)
     */
    public set scaleRatio(value: number) {
        if (this.xGizmo) {
            this.xGizmo.scaleRatio = value;
            this.yGizmo.scaleRatio = value;
            this.zGizmo.scaleRatio = value;
            this.uniformScaleGizmo.scaleRatio = value;
        }
    }
    public get scaleRatio() {
        return this.xGizmo.scaleRatio;
    }

    /**
     * Disposes of the gizmo
     */
    public dispose() {
        this.xGizmo.dispose();
        this.yGizmo.dispose();
        this.zGizmo.dispose();
        this.uniformScaleGizmo.dispose();
        this.onDragStartObservable.clear();
        this.onDragEndObservable.clear();
    }
}
